const valuesMatch = (left, right) => {
    if (!Number.isFinite(left) || !Number.isFinite(right)) return false
    const scale = Math.max(Math.abs(left), Math.abs(right), 1)
    return Math.abs(left - right) / scale < 0.02
}

const findPurchaseItem = (request, shipmentItem) =>
    (request?.items || []).find(item => {
        if (shipmentItem?.code && item.code === shipmentItem.code) return true
        if (shipmentItem?.id && item.id === shipmentItem.id) return true
        return (
            shipmentItem?.description &&
            item.description === shipmentItem.description
        )
    })

export const findAbleQuote = (request, shipmentItem) =>
    findPurchaseItem(request, shipmentItem)?.quotes?.find(
        quote => quote.able
    ) || null

/**
 * COP quotes store the peso amount in priceBeforeTRM and TRM = 1.
 * USD and EUR quotes store the foreign unit in priceBeforeTRM.
 */
export const isCopQuote = quote => {
    if (!quote) return false
    if (quote.currency === 'COP') return true
    if (quote.currency === 'USD' || quote.currency === 'EUR') return false
    return Number(quote.TRM) === 1
}

export const quoteDisplayCurrency = quote => {
    if (quote?.currency === 'COP' || quote?.currency === 'USD' || quote?.currency === 'EUR') {
        return quote.currency
    }
    return isCopQuote(quote) ? 'COP' : 'USD'
}

const nativeQuoteAmount = quote => {
    const amount = Number(quote?.priceBeforeTRM ?? quote?.unitPriceNoTaxBeforeTRM)
    return Number.isFinite(amount) && amount > 0 ? amount : null
}

const roundUnit = value => Math.round((value + Number.EPSILON) * 1000) / 1000

/**
 * Dollar unit shown in the import form.
 * For COP purchases this is empty until the shipment TRM is known.
 */
export const quoteUsdUnitPrice = quote => {
    if (!quote || isCopQuote(quote)) return null
    return nativeQuoteAmount(quote)
}

export const resolveUsdUnitPrice = (
    shipmentItem,
    quote,
    shipmentTrm,
    previousAuto
) => {
    const current = Number(shipmentItem?.unitPrice)
    const native = nativeQuoteAmount(quote)

    if (isCopQuote(quote) && native != null) {
        const trm = Number(shipmentTrm)
        if (trm > 0) {
            const usd = roundUnit(native / trm)
            const followsAuto =
                !Number.isFinite(current) ||
                current === 0 ||
                valuesMatch(current, native) ||
                (Number.isFinite(Number(previousAuto)) &&
                    valuesMatch(current, Number(previousAuto)))
            if (followsAuto) return usd
        }
        if (Number.isFinite(current)) return current
        return 0
    }

    const usd = native
    const cop = Number(quote?.price)
    if (
        usd != null &&
        Number.isFinite(cop) &&
        cop > 0 &&
        !valuesMatch(usd, cop)
    ) {
        if (!Number.isFinite(current) || current === 0) return usd
        if (valuesMatch(current, cop) && !valuesMatch(current, usd)) return usd
    }

    if (Number.isFinite(current)) return current
    return usd ?? 0
}

export const correctShipmentUsd = (request, shipment) => {
    const sourceItems = Array.isArray(shipment?.items) ? shipment.items : []
    let changed = false
    const items = sourceItems.map(item => {
        const unitPrice = resolveUsdUnitPrice(
            item,
            findAbleQuote(request, item),
            shipment?.trm
        )
        const quantity = Number(item.quantity) || 0
        const unitChanged = !valuesMatch(Number(item.unitPrice), unitPrice)
        if (unitChanged) changed = true
        const totalPrice = unitChanged
            ? unitPrice * quantity
            : Number(item.totalPrice) || unitPrice * quantity
        return { ...item, unitPrice, totalPrice }
    })

    if (!changed) return { ...shipment, items }

    const totalUSD = items.reduce(
        (sum, item) => sum + (Number(item.totalPrice) || 0),
        0
    )
    const trm = Number(shipment?.trm) || 0
    return {
        ...shipment,
        items,
        totalUSD,
        totalCOP: trm > 0 ? totalUSD * trm : Number(shipment?.totalCOP) || 0,
    }
}

export const withCorrectedComexUsd = request => {
    if (!request || !Array.isArray(request.comex)) return request
    return {
        ...request,
        comex: request.comex.map(shipment =>
            correctShipmentUsd(request, shipment)
        ),
    }
}
